import { IGenericModal, Modal } from 'components/Modal/Modal';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  buildPlatformPracticePayload,
  PlatformPracticeForm,
  practiceToFormDefaults
} from '../PlatformPracticeForm/PlatformPracticeForm';
import { IPlatformPractice, IPlatformPracticeFormFields } from 'types/platformPractice';
import {
  deletePlatformPractice,
  editPlatformPractice,
  getPlatformPractice
} from 'api/platformPractices';
import { showPlatformApiError } from 'views/PlatformView/platformApiError';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'components/_form';
import { yupResolver } from '@hookform/resolvers/yup';
import { platformPracticeEditSchema } from 'validation/platformPracticeSchemas';

interface Props extends IGenericModal {
  onSubmitSuccess?: () => void;
  onDeleteSuccess?: () => void;
  editingPracticeId: number;
}

export const EditPlatformPracticeForm = ({
  isOpen,
  onCancelClick,
  onSubmitSuccess,
  onDeleteSuccess,
  editingPracticeId
}: Props) => {
  const [editingPractice, setEditingPractice] = useState<IPlatformPractice>();
  const { t } = useTranslation();

  const methods = useForm<IPlatformPracticeFormFields>({
    mode: 'onChange',
    resolver: yupResolver(platformPracticeEditSchema)
  });

  const { handleSubmit, reset } = methods;

  const handleSubmitForm = async (data: IPlatformPracticeFormFields) => {
    try {
      await editPlatformPractice(editingPracticeId, buildPlatformPracticePayload(data));
      onSubmitSuccess?.();
    } catch (error) {
      showPlatformApiError(error, t);
    }
  };

  const handleDeletePractice = async () => {
    try {
      await deletePlatformPractice(editingPracticeId);
      onDeleteSuccess?.();
    } catch (error) {
      showPlatformApiError(error, t);
    }
  };

  const handleGetPractice = useCallback(async () => {
    if (!isOpen) return;

    try {
      const response = await getPlatformPractice(editingPracticeId);
      setEditingPractice(response.data);
    } catch (error) {
      showPlatformApiError(error, t);
    }
  }, [editingPracticeId, isOpen, t]);

  useEffect(() => {
    handleGetPractice();
  }, [handleGetPractice]);

  useEffect(() => {
    if (!editingPractice) {
      return;
    }

    reset(practiceToFormDefaults(editingPractice));
  }, [editingPractice, reset]);

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      headerTitle={t('platformPractices.editPractice')}
      mainButton={{
        action: handleSubmit(handleSubmitForm),
        variant: 'eucalyptus',
        label: t('buttons.save')
      }}
      isSecondButtonVisible
      gridTemplateColumns="70%"
      additionalFooter={
        <Button variant="red" onClick={handleDeletePractice}>
          {t('buttons.delete')}
        </Button>
      }
    >
      <FormProvider {...methods}>
        <form>
          <PlatformPracticeForm />
        </form>
      </FormProvider>
    </Modal>
  );
};
